import { useMemo } from 'react';
import { ColDef, FirstDataRenderedEvent, ICellRendererParams, ValueGetterParams, IHeaderParams, SortChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Alert, Label } from 'react-bootstrap'
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IDashContacts } from '@nkr/models/index';
import { GridWrapper } from './styles';

interface IProps {
    centerId: number,
    contactsData: IDashContacts[],
}

const Grid = (props: IProps) => {
    const DonorCellRenderer = (params: ICellRendererParams) => {
        const cellValue = params.value;
        const data = params.data;

        const cellStyle = {
            paddingTop: '3px',
        };

        if (data.id === null) {
            return <div style={cellStyle}>{cellValue}</div>;
        }

        return <div style={cellStyle}> <a href={'/center/' + data.centerId + '/donors/donor-intake/details/' + data.id}>{cellValue}</a></div>;
    }

    const SourceRenderer = (params: ICellRendererParams) => {
        const val = params.value;

        if (val == 'NKR') return <div className='label nkrLabl'>{val}</div>
        if (val == 'TXFR') return <div className='label txfrLabl'>{val}</div>
        if (val == 'RDN') return <div className='label rdnLabl'>{val}</div>
        if (val == 'MiSite') return <div className='label miLabl'>{val}</div>
        if (val == 'UDASH') return <div className='label label-warning'>{val}</div>
        if (val == 'DASH') return <div className='label dashLabl'>{val}</div>
        if (val == 'DCN') return <div className='label nkrLabl'>{val}</div>
        if (val == 'Liver') return <div className='label liverLabl'>{val}</div>
        return <div>{val}</div>
    }

    const cols: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: '#',
            width: 40,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
            sortable: false
        },
        {
            colId: 'source',
            field: 'source',
            headerName: 'Source',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Source</SortableHeaderComponent>,
            cellRendererFramework: SourceRenderer,
        },
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ',
            width: 55,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Organ</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let organType = params.value;
                return organType == "L" ? <span className="label label-warning">L</span> : <span className="label label-info">K</span>;
            }
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IDashContacts;
                return data.centerAlias != '' ? data.centerAlias : '--';
            }
        },
        {
            colId: 'donorName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            cellRendererFramework: DonorCellRenderer,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDashContacts;
                let { firstName, lastName } = data;

                if (lastName !== '' && firstName !== '') {
                    return `${firstName} ${lastName}`;
                }

                return '--';
            },
        },
        {
            colId: 'city',
            field: 'city',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>City</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.value != '' ? params.value : '--'
        },
        {
            colId: 'state',
            field: 'state',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>State</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.value != '' ? params.value : '--'
        },
        {
            colId: 'zip',
            field: 'postalCode',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Zip</SortableHeaderComponent>,
        },
        {
            colId: 'gender',
            field: 'gender',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Gender</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDashContacts;
                return data.gender.charAt(0)
            }
        },
        {
            colId: 'email',
            field: 'email',
            width: 300,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Email </SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let email = params.value;
                return <><a href={'mailto:' + email}>{email}</a></>
            }
        },
        {
            colId: 'age',
            field: 'age',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Age </SortableHeaderComponent>
        },
        {
            colId: 'daysPending',
            field: 'daysPending',
            width: 130,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Days<br /> Pending </SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IDashContacts;
                return Number(data.daysPending);
            }
        },
        {
            colId: 'currentStatus',
            field: 'currentStatus',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}> Current Status </SortableHeaderComponent>
        }
    ];

    const columnDefs = useMemo(() => cols, [])
    const rowData = useMemo(() => props.contactsData, [props.contactsData]);

    return (
        <GridWrapper>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: true,
                    flex: 1,
                }}
                columnDefs={columnDefs}
                suppressCellSelection={true}
                suppressContextMenu={true}
                rowData={rowData}
                headerHeight={50}
                noRowsOverlayComponentFramework={() => <Alert bsStyle='warning'>Try changing the date range to see more contacts </Alert>}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onSortChanged={(e: SortChangedEvent) => e.api.refreshCells()}
            />
        </GridWrapper>
    );
}


export default Grid;